import styled, { css, keyframes } from 'styled-components';

const DURATION = 10;

const notifEnter = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shrink = keyframes`
  from { width: 100%; }
  to { width: 0%; }
`;

const confettiBounce = keyframes`
  from { transform: translateY(10px) scale(0) rotate(-20deg); opacity: 0; }
  to { transform: translateY(0) scale(1) rotate(0deg); opacity: 1; }
`;

const confettiWiggle = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(14deg) scale(1.1); }
  75% { transform: rotate(-10deg) scale(1.05); }
`;

const sparkle = keyframes`
  0% { opacity: 0; transform: scale(0); }
  40% { opacity: 0.6; transform: scale(1.5); }
  100% { opacity: 0; transform: scale(0); }
`;

type ContainerProps = {
  $visible: boolean;
};

export const Overlay = styled.div`
  position: fixed;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

export const Container = styled.div<ContainerProps>`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1.25rem;
  background: #ffffff;
  border: 1px solid #e8ecf2;
  border-radius: 0.75rem;
  position: relative;
  overflow: hidden;
  min-width: 26rem;
  max-width: 36rem;
  width: max-content;
  animation: ${notifEnter} 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  transition: opacity 0.4s, transform 0.4s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  pointer-events: all;

  &:hover ${() => CountdownBar} {
    animation-play-state: paused;
  }

  ${({ $visible }) =>
    !$visible &&
    css`
      opacity: 0;
      transform: translateY(-8px);
    `}
`;

export const CountdownBar = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    background: ${theme.colors.brand.primary.default};
    animation: ${shrink} ${DURATION}s linear forwards;
  `}
`;

export const Confetti = styled.span`
  font-size: 1.625rem;
  flex-shrink: 0;
  display: inline-block;
  animation: ${confettiBounce} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both,
    ${confettiWiggle} 0.6s ease-in-out 0.6s 2;
`;

export const Sparkle = styled.span<{
  $top?: string;
  $right?: string;
  $bottom?: string;
  $delay: string;
  $size?: string;
}>`
  position: absolute;
  border-radius: 50%;
  background: #a78bfa;
  opacity: 0;
  animation: ${sparkle} 1.5s ease forwards;
  width: ${({ $size }) => $size || '4px'};
  height: ${({ $size }) => $size || '4px'};
  top: ${({ $top }) => $top};
  right: ${({ $right }) => $right};
  bottom: ${({ $bottom }) => $bottom};
  animation-delay: ${({ $delay }) => $delay};
`;

export const Body = styled.div`
  flex: 1;
`;

export const Text = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e293b;
`;

export const Highlight = styled.span`
  font-weight: 700;
  background: #f0fdf4;
  color: #16a34a;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  margin: 0 0.125rem;
`;

export const Sub = styled.div`
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-top: 2px;
`;

export const CloseButton = styled.button`
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background: #f1f5f9;
  border-radius: 0.375rem;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  padding: 0;

  &:hover {
    background: #e2e8f0;
    color: #64748b;
  }
`;
